import { useState, useEffect } from "react";
import { supabase, BRAND } from "./constants";
import { useIsMobile } from "./lib/responsive";
import Logo from "./Logo";
import { Network, ClipboardCheck, MessagesSquare, ShieldCheck, UserRound, Target, Handshake, BookOpen, MapPin, Eye, Mail, ChevronDown } from "lucide-react";

const PORTAL_URL = "https://candidates.rutment.com";

const LandingView = ({ setView, lang, setLang, setUserEmail = () => {}, setNotifRefreshKey = () => {} }) => {
  const [hoveredBtn, setHoveredBtn] = useState(null);
  const [openNav, setOpenNav] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const isMobile = useIsMobile();
  // Google (15/10): mismo proyecto que el portal. Sigue siendo solo por
  // invitación: si el email de Google no tiene cuenta, Supabase no la crea y
  // vuelve aquí con un error, que se enseña en el login. Una cuenta que existe
  // pero no es de ninguna empresa la saca App.js con su aviso, como con
  // contraseña.
  const [oauthError, setOauthError] = useState("");
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const h = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    if (q.get("error") || h.get("error")) {
      setOauthError(lang === "es"
        ? "Esa cuenta de Google no tiene acceso. El acceso es solo por invitación: entra con el email con el que te invitamos."
        : "That Google account has no access. Access is by invitation only: sign in with the email we invited.");
      setShowLogin(true);
      window.history.replaceState(null, "", window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleGoogle = async () => {
    setOauthError("");
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin + "/", queryParams: { prompt: "select_account" } },
    });
    if (error) setOauthError(error.message);
  };

  const handleLogin = async () => {
    setLoginLoading(true);
    setLoginError(false);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    setLoginLoading(false);
    if (error) {
      setLoginError(true);
      setPassword("");
      setTimeout(() => setLoginError(false), 2000);
    } else {
      setUserEmail(data.user.email);
      setView("dashboard");
      // Get location from IP then save login notification
      fetch("https://ipapi.co/json/")
        .then(r => r.json())
        .then(geo => {
          const loc = geo?.city && geo?.country_name ? `${geo.city}, ${geo.country_name}` : (geo?.country_name || null);
          supabase.from("admin_notifications").insert({
            type: "login",
            message: `Login: ${data.user.email}`,
            candidate_name: null,
            position_title: null,
            user_email: data.user.email,
            location: loc,
          }).then(() => setNotifRefreshKey(k => k + 1));
        })
        .catch(() => {
          supabase.from("admin_notifications").insert({
            type: "login",
            message: `Login: ${data.user.email}`,
            candidate_name: null,
            position_title: null,
            user_email: data.user.email,
            location: null,
          }).then(() => setNotifRefreshKey(k => k + 1));
        });
    }
  };

  const BG = "#0E0E12";
  const SURFACE = "#18181F";
  const BORDER_C = "#2A2A35";
  const TEXT_DIM = "#888899";

  const es = lang === "es";
  // Contenido de los menús (12/10): el recorrido real del producto —Mapping →
  // Cualificación → Evaluación → Referencias— más lo que ve el candidato, el
  // método (tres orígenes) y el modelo de servicio. Cada menú en grupos con
  // su encabezado; en móvil, los mismos grupos en un acordeón bajo el hero.
  const navItems = [
    {
      id: "product",
      label: es ? "Producto" : "Product",
      width: 560,
      groups: [
        {
          heading: es ? "Para empresas" : "For companies",
          items: [
            { icon: Network, title: "Mapping", desc: es ? "Validas el mercado antes de que contactemos a nadie: empresas objetivo y perfiles anónimos." : "You validate the market before we contact anyone: target companies and anonymous profiles." },
            { icon: ClipboardCheck, title: es ? "Cualificación" : "Qualification", desc: es ? "Cada candidato puntuado en 4 vectores, con el porqué escrito." : "Every candidate scored on 4 vectors, with the reasoning written down." },
            { icon: MessagesSquare, title: es ? "Evaluación guiada" : "Guided evaluation", desc: es ? "Guía de entrevista por vector, para que tu equipo pregunte lo que importa." : "An interview guide per vector, so your team asks what matters." },
            { icon: ShieldCheck, title: es ? "Referencias" : "References", desc: es ? "La verificación final antes de decidir." : "The final check before you decide." },
          ],
        },
        {
          heading: es ? "Para candidatos" : "For candidates",
          items: [
            { icon: UserRound, title: es ? "Portal del candidato" : "Candidate portal", desc: es ? "Sigue tu proceso, reúne referencias y evaluaciones para compartir, y haz el test de Archetypes." : "Follow your process, gather references and evaluations to share, and take the Archetypes test." },
          ],
        },
      ],
    },
    {
      id: "about",
      label: es ? "Sobre nosotros" : "About us",
      width: 360,
      groups: [
        {
          items: [
            { icon: Target, title: es ? "Nuestra misión" : "Our mission", desc: es ? "Hacer que contratar bien sea la norma, no la excepción." : "Making great hiring the norm, not the exception." },
            { icon: Handshake, title: es ? "Cómo trabajamos" : "How we work", desc: es ? "Executive search hecho a mano: nosotros hacemos la búsqueda y el juicio; la plataforma lo hace visible." : "Hand-made executive search: we do the search and the judgement; the platform makes it visible." },
            { icon: BookOpen, title: es ? "Nuestro método" : "Our method", desc: es ? "Investigación académica, técnicas de los mejores headhunters y experiencia en procesos reales." : "Academic research, the techniques of top headhunters and experience from real searches." },
            { icon: MapPin, title: es ? "Dónde estamos" : "Where we are", desc: es ? "Madrid, España" : "Madrid, Spain", href: "mailto:hello@rutment.com", linkText: "hello@rutment.com" },
          ],
        },
      ],
    },
    {
      id: "pricing",
      label: es ? "Precios" : "Pricing",
      width: 340,
      groups: [
        {
          items: [
            { icon: Handshake, title: es ? "Pagas por la búsqueda" : "You pay for the search", desc: es ? "No por licencias ni asientos de software." : "Not for software licences or seats." },
            { icon: Eye, title: es ? "Todo el proceso a la vista" : "The whole process in view", desc: es ? "Cada fase en la plataforma, del mapping a las referencias." : "Every stage on the platform, from mapping to references." },
            { icon: Mail, title: es ? "Hablemos" : "Let's talk", desc: es ? "Te contamos cómo encaja con tu búsqueda." : "We'll tell you how it fits your search.", href: "mailto:hello@rutment.com", linkText: "hello@rutment.com" },
          ],
        },
      ],
    },
  ];
  const [openMobile, setOpenMobile] = useState(null);

  // Una fila de menú: icono gris, título y descripción. Mismo marcado en el
  // desplegable de escritorio y en el acordeón del móvil.
  const menuRow = (c, i) => (
    <div key={i} style={{ display: "flex", gap: 12, padding: "10px 12px", borderRadius: 8 }}
      onMouseEnter={e => { if (!isMobile) e.currentTarget.style.background = "#22222C"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
      <c.icon size={18} strokeWidth={1.75} color="#9A9AB0" style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginBottom: 2 }}>{c.title}</div>
        <div style={{ fontSize: 12, color: TEXT_DIM, lineHeight: 1.5 }}>
          {c.desc}
          {c.href && <>{c.desc ? " · " : ""}<a href={c.href} style={{ color: "#A5A5FF", textDecoration: "none", fontWeight: 600 }}>{c.linkText}</a></>}
        </div>
      </div>
    </div>
  );
  const menuGroups = (item) => item.groups.map((g, gi) => (
    <div key={gi} style={gi ? { borderTop: `1px solid ${BORDER_C}`, marginTop: 6, paddingTop: 6 } : undefined}>
      {g.heading && <div style={{ fontSize: 10.5, fontWeight: 700, color: "#6E6E85", textTransform: "uppercase", letterSpacing: 0.6, padding: "8px 12px 4px" }}>{g.heading}</div>}
      <div style={{ display: "grid", gridTemplateColumns: !isMobile && g.items.length > 2 ? "1fr 1fr" : "1fr", columnGap: 4 }}>
        {g.items.map(menuRow)}
      </div>
    </div>
  ));

  return (
    <div
      style={{ minHeight: "100vh", width: "100%", background: BG, display: "flex", flexDirection: "column", fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif", position: "relative", overflow: "hidden" }}
      onClick={() => setOpenNav(null)}
    >
      {/* Radial glow */}
      <div className="rt-glow" style={{ position: "absolute", top: "40%", left: "50%", transform: "translate(-50%, -50%)", width: isMobile ? 480 : 700, height: isMobile ? 480 : 700, background: "radial-gradient(circle, rgba(62,62,248,0.15) 0%, transparent 70%)", pointerEvents: "none", zIndex: 0, willChange: "transform, opacity" }} />
      {/* Dot grid */}
      <div style={{ position: "absolute", inset: 0, zIndex: 0, backgroundImage: "radial-gradient(circle, #2A2A40 1px, transparent 1px)", backgroundSize: "36px 36px", opacity: 0.7 }} />

      {/* ── Navbar ── */}
      <div style={{ position: "relative", zIndex: 10, display: "flex", alignItems: "center", justifyContent: "space-between", padding: isMobile ? "0 16px" : "0 48px", height: isMobile ? 56 : 64, borderBottom: `1px solid ${BORDER_C}` }}>
        <Logo size={isMobile ? 20 : 22} color="#fff" />

        {/* Menús desplegables por hover: en móvil no caben ni se pueden pasar por encima; fuera */}
        {!isMobile && <div style={{ display: "flex", alignItems: "center", gap: 4 }} onClick={e => e.stopPropagation()}>
          {navItems.map(item => (
            <div key={item.id} style={{ position: "relative" }}>
              <button
                onMouseEnter={() => setOpenNav(item.id)}
                onClick={e => { e.stopPropagation(); setOpenNav(openNav === item.id ? null : item.id); }}
                style={{ background: "none", border: "none", cursor: "pointer", padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, color: openNav === item.id ? "#fff" : TEXT_DIM, display: "flex", alignItems: "center", gap: 5, transition: "color 0.15s" }}
              >
                {item.label}
                <ChevronDown size={14} strokeWidth={2} style={{ opacity: 0.6, transform: openNav === item.id ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
              </button>
              {openNav === item.id && (
                <div
                  className="rt-pop"
                  onMouseLeave={() => setOpenNav(null)}
                  style={{ position: "absolute", top: "calc(100% + 10px)", left: "50%", transform: "translateX(-50%)", background: SURFACE, border: `1px solid ${BORDER_C}`, borderRadius: 14, padding: "8px", width: item.width, boxSizing: "border-box", boxShadow: "0 24px 64px rgba(0,0,0,0.7)", zIndex: 100 }}
                >
                  <div style={{ position: "absolute", top: -5, left: "50%", transform: "translateX(-50%) rotate(45deg)", width: 10, height: 10, background: SURFACE, borderTop: `1px solid ${BORDER_C}`, borderLeft: `1px solid ${BORDER_C}` }} />
                  {menuGroups(item)}
                </div>
              )}
            </div>
          ))}
        </div>}

        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 2 : 6 }}>
          {["es", "en"].map(l => (
            <button key={l} onClick={() => setLang(l)} style={{ background: "none", border: "none", fontSize: isMobile ? 13 : 12, fontWeight: 700, color: lang === l ? "#fff" : TEXT_DIM, cursor: "pointer", padding: isMobile ? "0 10px" : "4px 6px", letterSpacing: 0.4, ...(isMobile ? { minHeight: 40, minWidth: 44 } : {}) }}>
              {l === "es" ? "ESP" : "ENG"}
            </button>
          ))}
        </div>
      </div>

      {/* ── Hero ── */}
      <div style={{ flex: 1, position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", ...(isMobile ? { padding: "32px 16px 48px" } : { paddingBottom: 80 }) }}>
        {/* Entrada escalonada (rt-fu + retraso): solo opacidad y desplazamiento,
            que el navegador anima sin recalcular la página */}
        <div className="rt-fu" style={{ marginBottom: isMobile ? 32 : 44 }}>
          <Logo size={isMobile ? 36 : 46} color="#fff" blink />
        </div>
        <h1 className="rt-fu" style={{ animationDelay: "0.08s", fontSize: isMobile ? 32 : 54, fontWeight: 800, color: "#fff", margin: "0 0 14px 0", letterSpacing: isMobile ? -1 : -2, textAlign: "center", lineHeight: 1.08 }}>
          {lang === "es" ? "Fichajes clave para founders" : "Key hires for founders"}
        </h1>
        <p className="rt-fu" style={{ animationDelay: "0.16s", fontSize: isMobile ? 16 : 18, color: TEXT_DIM, fontWeight: 500, margin: isMobile ? "0 0 36px 0" : "0 0 60px 0", textAlign: "center" }}>
          {lang === "es" ? "Contrata mejor, crece más" : "Hire good, grow better"}
        </p>
        {/* Móvil: los dos accesos apilados y a todo el ancho */}
        <div className="rt-fu" style={{ animationDelay: "0.24s", display: "flex", gap: isMobile ? 12 : 14, ...(isMobile ? { flexDirection: "column", width: "100%", maxWidth: 360 } : {}) }}>
          <button
            onMouseEnter={() => setHoveredBtn("company")}
            onMouseLeave={() => setHoveredBtn(null)}
            onClick={() => setShowLogin(true)}
            style={{ background: hoveredBtn === "company" ? "#2B2BF0" : BRAND, color: "#fff", border: "none", borderRadius: 14, padding: "15px 36px", fontSize: 15, fontWeight: 700, cursor: "pointer", letterSpacing: -0.2, boxShadow: hoveredBtn === "company" ? "0 12px 40px rgba(62,62,248,0.55)" : "0 6px 24px rgba(62,62,248,0.3)", transition: "all 0.18s ease", transform: hoveredBtn === "company" ? "translateY(-2px)" : "translateY(0)", ...(isMobile ? { width: "100%", minHeight: 52 } : {}) }}
          >
            {lang === "es" ? "Acceso empresa" : "Company access"}
          </button>
          <button
            onMouseEnter={() => setHoveredBtn("candidate")}
            onMouseLeave={() => setHoveredBtn(null)}
            onClick={() => window.open(PORTAL_URL, "_blank")}
            style={{ background: hoveredBtn === "candidate" ? "#2B2BF0" : BRAND, color: "#fff", border: "none", borderRadius: 14, padding: "15px 36px", fontSize: 15, fontWeight: 700, cursor: "pointer", letterSpacing: -0.2, boxShadow: hoveredBtn === "candidate" ? "0 12px 40px rgba(62,62,248,0.55)" : "0 6px 24px rgba(62,62,248,0.3)", transition: "all 0.18s ease", transform: hoveredBtn === "candidate" ? "translateY(-2px)" : "translateY(0)", ...(isMobile ? { width: "100%", minHeight: 52 } : {}) }}
          >
            {lang === "es" ? "Acceso candidato" : "Candidate access"}
          </button>
        </div>

        {/* Móvil: los menús (que en escritorio se abren al pasar el ratón) como
            acordeón bajo los accesos. Tocar una fila la abre o la cierra. */}
        {isMobile && (
          <div className="rt-fu" style={{ animationDelay: "0.32s", width: "100%", maxWidth: 360, marginTop: 36, borderTop: `1px solid ${BORDER_C}` }}>
            {navItems.map(item => {
              const open = openMobile === item.id;
              return (
                <div key={item.id} style={{ borderBottom: `1px solid ${BORDER_C}` }}>
                  <button type="button" onClick={() => setOpenMobile(open ? null : item.id)} aria-expanded={open}
                    style={{ width: "100%", minHeight: 52, display: "flex", alignItems: "center", justifyContent: "space-between", background: "none", border: "none", padding: "0 4px", color: open ? "#fff" : "#C9C9D6", fontSize: 15, fontWeight: 600, fontFamily: "inherit", cursor: "pointer" }}>
                    {item.label}
                    <ChevronDown size={18} strokeWidth={2} style={{ opacity: 0.6, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
                  </button>
                  {open && <div className="rt-pop-m" style={{ paddingBottom: 10 }}>{menuGroups(item)}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Login modal ── */}
      {showLogin && (
        <div style={isMobile
            ? { position: "fixed", inset: 0, background: "#18181F", zIndex: 200, display: "flex", flexDirection: "column", overflowY: "auto", paddingBottom: "env(safe-area-inset-bottom)" }
            : { position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={isMobile ? undefined : () => { setShowLogin(false); setPassword(""); setLoginError(false); }}>
          {/* Móvil: hoja a pantalla completa con su botón de cerrar */}
          {isMobile && (
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "8px 8px 0" }}>
              <button onClick={() => { setShowLogin(false); setPassword(""); setLoginError(false); }}
                aria-label={lang === "es" ? "Cerrar" : "Close"}
                style={{ width: 40, height: 40, background: "none", border: "none", color: TEXT_DIM, fontSize: 26, lineHeight: 1, cursor: "pointer" }}>×</button>
            </div>
          )}
          <div style={isMobile
              ? { padding: "24px 16px 32px", width: "100%", boxSizing: "border-box" }
              : { background: "#18181F", border: "1px solid #2A2A35", borderRadius: 18, padding: "36px 40px", width: 360, boxShadow: "0 32px 80px rgba(0,0,0,0.8)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ marginBottom: 6 }}><Logo size={20} color="#fff" /></div>
            <div style={{ fontSize: 13, color: "#888899", marginBottom: 28 }}>{lang === "es" ? "Acceso empresa" : "Company access"}</div>
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setLoginError(false); }}
              onKeyDown={e => e.key === "Enter" && handleLogin()}
              placeholder="Email"
              autoFocus
              autoCapitalize={isMobile ? "none" : undefined}
              style={{ width: "100%", boxSizing: "border-box", background: "#0E0E12", border: `1.5px solid ${loginError ? "#EF4444" : "#2A2A35"}`, borderRadius: 10, padding: "12px 16px", fontSize: isMobile ? 16 : 14, color: "#fff", outline: "none", marginBottom: 10, fontFamily: "'DM Sans', sans-serif", transition: "border-color 0.2s", ...(isMobile ? { minHeight: 48 } : {}) }}
            />
            <input
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); setLoginError(false); }}
              onKeyDown={e => e.key === "Enter" && handleLogin()}
              placeholder={lang === "es" ? "Contraseña" : "Password"}
              style={{ width: "100%", boxSizing: "border-box", background: "#0E0E12", border: `1.5px solid ${loginError ? "#EF4444" : "#2A2A35"}`, borderRadius: 10, padding: "12px 16px", fontSize: isMobile ? 16 : 14, color: "#fff", outline: "none", marginBottom: 8, fontFamily: "'DM Sans', sans-serif", transition: "border-color 0.2s", ...(isMobile ? { minHeight: 48 } : {}) }}
            />
            {loginError && <div style={{ fontSize: 12, color: "#EF4444", marginBottom: 12 }}>{lang === "es" ? "Email o contraseña incorrectos" : "Incorrect email or password"}</div>}
            {!loginError && <div style={{ marginBottom: 20 }} />}
            <button onClick={handleLogin} disabled={loginLoading} style={{ width: "100%", background: loginLoading ? "#555" : BRAND, color: "#fff", border: "none", borderRadius: 10, padding: "13px", fontSize: 15, fontWeight: 700, cursor: loginLoading ? "not-allowed" : "pointer", letterSpacing: -0.2, ...(isMobile ? { minHeight: 50 } : {}) }}>
              {loginLoading ? (lang === "es" ? "Entrando..." : "Signing in...") : (lang === "es" ? "Entrar" : "Enter")}
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "16px 0" }}>
              <div style={{ flex: 1, height: 1, background: "#2A2A35" }} />
              <span style={{ fontSize: 12, color: "#666" }}>{lang === "es" ? "o" : "or"}</span>
              <div style={{ flex: 1, height: 1, background: "#2A2A35" }} />
            </div>
            <button type="button" onClick={handleGoogle}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: "#fff", color: "#111", border: "none", borderRadius: 10, padding: "12px", fontSize: 14, fontWeight: 600, fontFamily: "inherit", cursor: "pointer", ...(isMobile ? { minHeight: 50, fontSize: 15 } : {}) }}>
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
              {lang === "es" ? "Continuar con Google" : "Continue with Google"}
            </button>
            {oauthError && <div style={{ fontSize: 12, color: "#EF4444", marginTop: 12, lineHeight: 1.5 }}>{oauthError}</div>}
            <div style={{ fontSize: 12, color: "#666", textAlign: "center", marginTop: 16 }}>{lang === "es" ? "Acceso solo por invitación." : "Access by invitation only."}</div>
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      <div style={{ position: "relative", zIndex: 1, display: "flex", justifyContent: "center", alignItems: "center", gap: isMobile ? 12 : 20, paddingBottom: isMobile ? "calc(20px + env(safe-area-inset-bottom))" : 28, borderTop: `1px solid ${BORDER_C}`, paddingTop: 20, ...(isMobile ? { flexWrap: "wrap", paddingLeft: 16, paddingRight: 16 } : {}) }}>
        <span style={{ fontSize: 12, color: "#444", fontWeight: 500 }}>© 2026 Rutment</span>
        <span style={{ color: "#333" }}>·</span>
        <button onClick={() => {}} style={{ background: "none", border: "none", fontSize: 12, color: "#444", fontWeight: 500, cursor: "pointer", padding: 0 }} onMouseEnter={e => e.currentTarget.style.color = "#888"} onMouseLeave={e => e.currentTarget.style.color = "#444"}>
          {lang === "es" ? "Política de privacidad" : "Privacy policy"}
        </button>
        <span style={{ color: "#333" }}>·</span>
        <button onClick={() => {}} style={{ background: "none", border: "none", fontSize: 12, color: "#444", fontWeight: 500, cursor: "pointer", padding: 0 }} onMouseEnter={e => e.currentTarget.style.color = "#888"} onMouseLeave={e => e.currentTarget.style.color = "#444"}>
          {lang === "es" ? "Política de cookies" : "Cookies policy"}
        </button>
      </div>
    </div>
  );
};

// Animaciones de la portada, solo CSS (12/10). Todas animan opacidad o
// transform, que el navegador hace en la GPU sin recalcular la página: no pesan
// ni en el móvil. Con "reducir movimiento" activado en el sistema, se apagan.
//   rt-fu    entrada escalonada del hero (una vez)
//   rt-blink el bloque del logo parpadea como un cursor
//   rt-glow  el halo azul respira despacio
//   rt-pop   aparición de los desplegables (y rt-pop-m, del acordeón móvil)
if (typeof document !== "undefined" && !document.getElementById("rutment-spin")) {
  const s = document.createElement("style");
  s.id = "rutment-spin";
  s.textContent = `
@keyframes spin { to { transform: rotate(360deg); } }
@keyframes rt-fade-up { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
@keyframes rt-blink { 0%, 55% { opacity: 1; } 56%, 100% { opacity: 0; } }
@keyframes rt-glow { 0%, 100% { opacity: 0.75; transform: translate(-50%, -50%) scale(1); } 50% { opacity: 1; transform: translate(-50%, -50%) scale(1.08); } }
@keyframes rt-pop { from { opacity: 0; transform: translate(-50%, 6px); } to { opacity: 1; transform: translate(-50%, 0); } }
@keyframes rt-pop-m { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: none; } }
.rt-fu { animation: rt-fade-up 0.7s cubic-bezier(0.2, 0.7, 0.2, 1) both; }
.rt-blink { animation: rt-blink 1.1s steps(1, end) infinite; }
.rt-glow { animation: rt-glow 8s ease-in-out infinite; }
.rt-pop { animation: rt-pop 0.16s ease-out both; }
.rt-pop-m { animation: rt-pop-m 0.18s ease-out both; }
@media (prefers-reduced-motion: reduce) {
  .rt-fu, .rt-blink, .rt-glow, .rt-pop, .rt-pop-m { animation: none !important; }
}`;
  document.head.appendChild(s);
}

export default LandingView;
